/**
 * Frontend view script for Hero Banner background video.
 *
 * - Respects prefers-reduced-motion (show poster, stop video/embeds).
 * - Ensures muted autoplay for Media Library videos across browsers.
 * - Switches optional mobile Vimeo/YouTube embeds at the theme breakpoint.
 * - Adds dns-prefetch / preconnect only for the active external provider.
 */

const BLOCK_SELECTOR = '.wp-block-alteia-hero-banner.alteia-hero-banner';
const REDUCED_MOTION_CLASS = 'alteia-hero-banner--reduced-motion';
/** Matches Hero Banner SCSS mobile focal-point breakpoint. */
const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

const PROVIDER_ORIGINS = {
	vimeo: [ 'https://player.vimeo.com', 'https://i.vimeocdn.com' ],
	youtube: [ 'https://www.youtube-nocookie.com', 'https://i.ytimg.com' ],
};

/**
 * @return {boolean} Whether the user prefers reduced motion.
 */
function prefersReducedMotion() {
	return (
		typeof window.matchMedia === 'function' &&
		window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
	);
}

/**
 * @return {boolean} Whether the viewport is at the mobile hero breakpoint.
 */
function isMobileViewport() {
	return (
		typeof window.matchMedia === 'function' &&
		window.matchMedia( MOBILE_MEDIA_QUERY ).matches
	);
}

/**
 * Ensure a single preconnect / dns-prefetch link exists for a given origin.
 *
 * @param {string} origin Absolute origin.
 * @param {string} rel    Link rel.
 * @return {void}
 */
function ensureResourceHint( origin, rel ) {
	if ( ! origin || ! document.head ) {
		return;
	}

	const selector = `link[rel="${ rel }"][href="${ origin }"]`;
	if ( document.head.querySelector( selector ) ) {
		return;
	}

	const link = document.createElement( 'link' );
	link.rel = rel;
	link.href = origin;
	if ( rel === 'preconnect' ) {
		link.crossOrigin = 'anonymous';
	}
	document.head.appendChild( link );
}

/**
 * Add resource hints for the active external provider on this page.
 *
 * @param {string} provider vimeo|youtube.
 * @return {void}
 */
function hintProvider( provider ) {
	const origins = PROVIDER_ORIGINS[ provider ];
	if ( ! origins ) {
		return;
	}

	origins.forEach( ( origin ) => {
		ensureResourceHint( origin, 'dns-prefetch' );
		ensureResourceHint( origin, 'preconnect' );
	} );
}

/**
 * Stop and hide decorative background media when reduced motion is preferred.
 *
 * @param {HTMLElement} block Hero banner root.
 * @return {void}
 */
function applyReducedMotion( block ) {
	block.classList.add( REDUCED_MOTION_CLASS );

	block
		.querySelectorAll( '.alteia-hero-banner__background-video' )
		.forEach( ( video ) => {
			try {
				video.pause();
			} catch ( error ) {
				// Ignore pause failures on detached nodes.
			}
			video.removeAttribute( 'autoplay' );
			video.removeAttribute( 'src' );
			video.load();
			video.setAttribute( 'hidden', 'hidden' );
		} );

	block
		.querySelectorAll( '.alteia-hero-banner__background-iframe' )
		.forEach( ( iframe ) => {
			iframe.removeAttribute( 'src' );
			iframe.setAttribute( 'hidden', 'hidden' );
			iframe.remove();
		} );
}

/**
 * Keep Media Library videos muted and playing when allowed.
 *
 * @param {HTMLVideoElement} video Video element.
 * @return {void}
 */
function ensureMediaAutoplay( video ) {
	video.muted = true;
	video.defaultMuted = true;
	video.setAttribute( 'muted', '' );
	video.setAttribute( 'playsinline', '' );
	video.setAttribute( 'webkit-playsinline', 'true' );
	video.playsInline = true;

	const playPromise = video.play();
	if ( playPromise && typeof playPromise.catch === 'function' ) {
		playPromise.catch( () => {
			// Autoplay can still be blocked; poster remains visible.
		} );
	}
}

/**
 * Apply desktop/mobile embed URL when an optional mobile Vimeo/YouTube exists.
 *
 * Uses a single iframe so only one provider player is active.
 *
 * @param {HTMLElement} background Background wrapper.
 * @return {void}
 */
function applyResponsiveEmbedSource( background ) {
	const desktopEmbed = background.getAttribute(
		'data-alteia-hero-video-embed'
	);
	const mobileEmbed = background.getAttribute(
		'data-alteia-hero-video-embed-mobile'
	);
	const iframe = background.querySelector(
		'.alteia-hero-banner__background-iframe'
	);

	if ( ! iframe || ! desktopEmbed ) {
		return;
	}

	if ( ! mobileEmbed ) {
		if ( ! iframe.getAttribute( 'src' ) ) {
			iframe.setAttribute( 'src', desktopEmbed );
		}
		return;
	}

	const syncSource = () => {
		const targetEmbed = isMobileViewport() ? mobileEmbed : desktopEmbed;
		const currentSrc = iframe.getAttribute( 'src' ) || '';

		if ( currentSrc !== targetEmbed ) {
			iframe.setAttribute( 'src', targetEmbed );
		}
	};

	syncSource();

	if ( typeof window.matchMedia !== 'function' ) {
		return;
	}

	const mediaQueryList = window.matchMedia( MOBILE_MEDIA_QUERY );
	const onChange = () => {
		syncSource();
	};

	if ( typeof mediaQueryList.addEventListener === 'function' ) {
		mediaQueryList.addEventListener( 'change', onChange );
	} else if ( typeof mediaQueryList.addListener === 'function' ) {
		mediaQueryList.addListener( onChange );
	}
}

/**
 * @param {HTMLElement} block Hero banner root.
 * @return {void}
 */
function initBlock( block ) {
	if ( block.dataset.alteiaHeroVideoInit === '1' ) {
		return;
	}
	block.dataset.alteiaHeroVideoInit = '1';

	const background = block.querySelector( '.alteia-hero-banner__background' );
	const provider = background
		? background.getAttribute( 'data-alteia-hero-video-provider' )
		: '';

	if ( provider === 'vimeo' || provider === 'youtube' ) {
		hintProvider( provider );
	}

	if ( prefersReducedMotion() ) {
		applyReducedMotion( block );
		return;
	}

	if ( background && ( provider === 'vimeo' || provider === 'youtube' ) ) {
		applyResponsiveEmbedSource( background );
	}

	block
		.querySelectorAll( '.alteia-hero-banner__background-video' )
		.forEach( ( video ) => {
			ensureMediaAutoplay( video );
		} );
}

/**
 * @return {void}
 */
function init() {
	document.querySelectorAll( BLOCK_SELECTOR ).forEach( initBlock );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
